/** @format */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaEye } from 'react-icons/fa';
import Header from '../../components/common/Header';
// lazy load footer
const Footer = React.lazy(() => import('../../components/common/Footer'));
import axios from 'axios';
import { useAuthStore } from '../../context/useAuthStore';
import { ROUTES } from '../../constant/routes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface Blog {
	_id: string;
	title: string;
	content: string;
	description?: string;
	image: string;
	date: string;
	slug: string;
	count: number;
}

const BlogDetail: React.FC = () => {
	const { token, clearSession } = useAuthStore();
	const { slug } = useParams<{ slug: string }>();
	const [blog, setBlog] = useState<Blog | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		const fetchBlog = async () => {
			if (!slug) return;
			try {
				const headers: any = {
					'Content-Type': 'application/json',
				};

				if (token) {
					headers.Authorization = `Bearer ${token}`;
				}

				const res = await axios.get(`${API_BASE_URL}/blog`, {
					headers,
					params: { slug: slug },
					withCredentials: true,
				});

				const successData = res.data.success;
				// usage based on user input: success is an array of objects
				const data =
					Array.isArray(successData) && successData.length > 0
						? successData[0]
						: null;

				if (data) {
					// Map if necessary, similar to Blogs.tsx
					let imageUrl = data.image || '/what-we-do.png';
					if (data.image) {
						const filename = data.image.split('/').pop();
						imageUrl = `https://www.llb360.com/uploads/blogs/${filename}`;
					}

					const mappedBlog: Blog = {
						_id: data.blogId || data._id,
						title: data.title || 'Untitled',
						content: data.content || data.description || '',
						image: imageUrl,
						date:
							data.created || data.date // user response says 'created' field exists
								? new Date(data.created || data.date).toLocaleDateString(
										'en-GB',
										{
											day: 'numeric',
											month: 'short',
											year: 'numeric',
										}
								  )
								: 'Recent',
						slug: data.slug || slug,
						count: data.views || data.count || 0,
					};
					setBlog(mappedBlog);
				}
			} catch (error) {
				console.error(error);
				if (axios.isAxiosError(error)) {
					const errorMessage = error.response?.data?.message || '';
					if (
						errorMessage.includes('Session expired') ||
						error.response?.status === 401
					) {
						clearSession('expired');
					}
				}
			} finally {
				setLoading(false);
			}
		};

		fetchBlog();
	}, [slug, token]);

	if (loading) {
		return (
			<div className='flex justify-center items-center h-screen bg-white'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-myButtonBg'></div>
			</div>
		);
	}

	if (!blog) {
		return (
			<body className='bg-white p-0! overflow-x-hidden'>
				<Header />
				<main className='min-h-screen flex items-center justify-center'>
					<p className='text-lg text-gray-600'>Blog not found.</p>
				</main>
				<React.Suspense
					fallback={<div className='h-20 bg-gray-100 animate-pulse' />}>
					<Footer />
				</React.Suspense>
			</body>
		);
	}

	return (
		<body className='bg-white p-0! overflow-x-hidden'>
			<Header />
			<main className='w-full py-20 px-4 bg-[#F6FBFD] scroll-mt-24'>
				<div className='max-w-4xl mx-auto'>
					{/* Back Button */}
					<Link
						to={ROUTES.BLOGS}
						className='inline-flex items-center gap-2 text-sm text-myButtonBg mb-6 hover:underline'>
						<FaArrowLeft /> Back to Blogs
					</Link>

					{/* Title */}
					<h1 className='text-3xl md:text-4xl font-bold text-myButtonBg leading-tight'>
						{blog.title}
					</h1>

					{/* Meta */}
					<div className='flex items-center gap-6 mt-4 text-sm text-gray-600'>
						<span>{blog.date}</span>
						<span className='flex items-center gap-1'>
							<FaEye /> {blog.count}
						</span>
					</div>

					{/* Image */}
					<div className='mt-8'>
						<img
							src={blog.image}
							alt={blog.title}
							className='w-full rounded-xl shadow-lg object-cover aspect-video object-top'
						/>
					</div>

					{/* Content */}
					<section
						className='mt-10 text-gray-800 leading-relaxed text-base space-y-6 blogContent'
						dangerouslySetInnerHTML={{
							__html: (blog.content || '').replace(
								/https?:\/\/(?:www\.)?llb360\.com\/uploads\/blogs\//g,
								'https://www.llb360.com/uploads/blogs/'
							),
						}}
					/>

					{/* CTA */}
					<div className='mt-12 bg-white rounded-lg p-6 shadow-md text-center'>
						<h3 className='text-lg font-semibold text-myButtonBg'>
							Want exam-ready notes and structured answers?
						</h3>
						<p className='text-sm text-gray-600 mt-2'>
							Explore subject-wise legal content designed specifically for law
							exams.
						</p>
						<Link
							to='/'
							className='inline-block mt-4 bg-myButtonBg text-white px-6 py-2 rounded-md hover:bg-myButtonHover'>
							Explore Subjects
						</Link>
					</div>
				</div>
			</main>
			<React.Suspense
				fallback={<div className='h-20 bg-gray-100 animate-pulse' />}>
				<Footer />
			</React.Suspense>
		</body>
	);
};

export default BlogDetail;
